import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  catalogueItemApi,
  cataloguePhotoApi,
  productApi,
  productCategoryApi,
  toAbsUrl,
} from "../api/api";
import "./public-catalogue.css";
import SearchableDropdown from "../components/SearchableDropdown";

/* ── Toast notification ── */
function Toast({ message, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        background: "#110F0E",
        color: "#FDF8F0",
        padding: "12px 24px",
        borderRadius: 8,
        border: "1px solid rgba(197, 160, 89, 0.4)",
        boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
        zIndex: 2000,
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 10,
        letterSpacing: "0.04em",
      }}
    >
      <span style={{ color: "#D4AF37", fontWeight: "bold" }}>✓</span>
      <span>{message}</span>
    </div>
  );
}

/* ── Full-Page Product Detail Showcase ── */
function ProductDetailShowcase({
  item,
  product,
  category,
  photos,
  onBack,
  onShare,
  relatedItems = [],
  onSelectItem,
  itemPhotosMap = {},
  productMap = new Map(),
  wishlist = [],
  onToggleWishlist,
}) {
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isCopiedCode, setIsCopiedCode] = useState(false);
  const [isCopiedLink, setIsCopiedLink] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  const itemId = item.catalogueItemID ?? item.catalogueItemId ?? item.CatalogueItemID ?? item.id;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setActivePhotoIdx(0);
  }, [itemId]);

  const getPhotoUrl = (ph) => {
    if (!ph) return null;
    const rawPath =
      ph.filepath ||
      ph.Filepath ||
      ph.filename ||
      ph.Filename ||
      (Array.isArray(ph.photos) ? ph.photos[0] : ph.photos) ||
      (Array.isArray(ph.Photos) ? ph.Photos[0] : ph.Photos);
    if (!rawPath || typeof rawPath !== "string") return null;
    return toAbsUrl(rawPath);
  };

  const safePhotos = Array.isArray(photos) ? photos : [];
  const activePhoto = safePhotos[activePhotoIdx] || safePhotos[0];
  const activeSrc = getPhotoUrl(activePhoto);
  const productCode = product?.productCode || "RJ-001";
  const isWishlisted = wishlist.includes(itemId);

  const handleCopyCode = () => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(productCode);
      setIsCopiedCode(true);
      setTimeout(() => setIsCopiedCode(false), 2000);
    }
  };

  const handleShareLink = async () => {
    const shareUrl = `${window.location.origin}/catalogue?item=${itemId}`;
    if (navigator?.share) {
      try {
        await navigator.share({
          title: `Reva Jewellers — ${item.displayName}`,
          text: `Explore ${item.displayName} (Ref: ${productCode}) at Reva Jewellers`,
          url: shareUrl,
        });
        return;
      } catch { }
    }
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(shareUrl);
      setIsCopiedLink(true);
      setToastMessage("Product link copied to clipboard");
      setTimeout(() => setIsCopiedLink(false), 2500);
    } else {
      onShare(item, productCode);
    }
  };

  // Keyboard navigation for fullscreen lightbox
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsFullscreen(false);
        setIsZoomed(false);
      } else if (e.key === "ArrowLeft" && safePhotos.length > 1) {
        setActivePhotoIdx((prev) => (prev > 0 ? prev - 1 : safePhotos.length - 1));
        setIsZoomed(false);
      } else if (e.key === "ArrowRight" && safePhotos.length > 1) {
        setActivePhotoIdx((prev) => (prev < safePhotos.length - 1 ? prev + 1 : 0));
        setIsZoomed(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen, safePhotos.length]);

  return (
    <div className="rv-pub-detail-page">
      {toastMessage && <Toast message={toastMessage} onClose={() => setToastMessage(null)} />}

      {/* ── Breadcrumb & Back Navigation Bar ── */}
      <div className="rv-pub-breadcrumb-bar">
        <button type="button" className="rv-pub-back-btn" onClick={onBack}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span>Back to Collection</span>
        </button>

        <nav className="rv-pub-breadcrumb-trail" aria-label="Breadcrumb">
          <button type="button" onClick={onBack} className="rv-pub-breadcrumb-link">
            Showcase
          </button>
          <span className="rv-pub-breadcrumb-sep">/</span>
          <span className="rv-pub-breadcrumb-cat">{category?.categoryName || item.displaySection || "Jewellery"}</span>
          <span className="rv-pub-breadcrumb-sep rv-pub-breadcrumb-sep--item">/</span>
          <span className="rv-pub-breadcrumb-current" title={item.displayName}>{item.displayName}</span>
        </nav>
      </div>

      {/* ── Main 2-Column Product Showcase ── */}
      <div className="rv-pub-detail-grid">
        {/* Left: Product Image Gallery */}
        <div className="rv-pub-detail-gallery-card">
          <div
            className="rv-pub-detail-main-photo-box"
            onClick={() => activeSrc && setIsFullscreen(true)}
            title={activeSrc ? "Click to open fullscreen image" : ""}
          >
            {activeSrc ? (
              <>
                <img src={activeSrc} alt={item.displayName} className="rv-pub-detail-main-img" />
                <button
                  type="button"
                  className="rv-pub-zoom-trigger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsFullscreen(true);
                  }}
                  title="View full resolution"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    <line x1="11" y1="8" x2="11" y2="14" />
                    <line x1="8" y1="11" x2="14" y2="11" />
                  </svg>
                </button>
              </>
            ) : (
              <div className="rv-pub-detail-no-img">
                <span style={{ fontSize: 32 }}>💎</span>
                <span>Fine Atelier Creation</span>
              </div>
            )}

            {/* Subtle Featured Badge (Commented out per UX review) */}
            {/* {item.isFeatured && (
              <div className="rv-pub-card-top-badges">
                <span className="rv-badge rv-badge--gold">⭐ FEATURED HIGHLIGHT</span>
              </div>
            )} */}
          </div>

          {/* Genuine Thumbnails Strip */}
          {safePhotos.length > 1 && (
            <div className="rv-pub-thumb-slider">
              {safePhotos.map((ph, idx) => {
                const src = getPhotoUrl(ph);
                return (
                  <button
                    key={ph.photoID || idx}
                    type="button"
                    className={`rv-pub-thumb-btn ${activePhotoIdx === idx ? "rv-pub-thumb-btn--active" : ""}`}
                    onClick={() => {
                      setActivePhotoIdx(idx);
                      setIsZoomed(false);
                    }}
                    title={`View angle ${idx + 1}`}
                  >
                    {src ? <img src={src} alt={`Thumbnail ${idx + 1}`} className="rv-pub-thumb-img" /> : <span>#{idx + 1}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: Product Information Panel */}
        <div className="rv-pub-detail-info-card">
          {/* Top Metadata & Sharing Bar */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: "#8F7B67", textTransform: "uppercase" }}>
                REF: {productCode}
              </span>
              <button
                type="button"
                onClick={handleCopyCode}
                title="Copy reference code"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: isCopiedCode ? "#2E7D32" : "#8F7B67",
                  fontSize: 11,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  padding: "2px 4px",
                }}
              >
                {isCopiedCode ? "✓ Copied" : "📋"}
              </button>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              {onToggleWishlist && (
                <button
                  type="button"
                  className="rv-pub-btn-outline"
                  style={{ padding: "5px 12px", fontSize: 11, color: isWishlisted ? "#D4AF37" : "inherit" }}
                  onClick={() => onToggleWishlist(itemId, item.displayName)}
                >
                  {isWishlisted ? "♥ Wishlisted" : "♡ Save"}
                </button>
              )}
              <button
                type="button"
                className="rv-pub-btn-outline"
                style={{ padding: "5px 12px", fontSize: 11 }}
                onClick={handleShareLink}
              >
                {isCopiedLink ? "✓ Copied" : "🔗 Share Link"}
              </button>
            </div>
          </div>

          {/* Product Name & Short Description */}
          <div>
            <h1 className="rv-pub-detail-title">{item.displayName}</h1>
            {item.descriptionLbl && <p className="rv-pub-detail-sublabel">{item.descriptionLbl}</p>}
          </div>

          {/* Pricing Box */}
          {/* <div className="rv-pub-detail-price-box">
            <span className="rv-pub-detail-price">
              {product?.sellingPrice || product?.price || item?.price
                ? `₹${Number(product?.sellingPrice || product?.price || item?.price).toLocaleString("en-IN")}`
                : product?.pricingType === "Fixed"
                  ? "Fixed Price"
                  : "Price on Request"}
            </span>
          </div> */}

          {/* Primary Product Details (Factual Table) */}
          <div className="rv-pub-detail-specs-section">
            <h4 className="rv-pub-detail-section-title">Product Details</h4>

            {item.description && (
              <div
                className="ql-editor"
                style={{
                  padding: 0,
                  minHeight: "auto",
                  fontSize: 13.5,
                  color: "#5C554F",
                  lineHeight: 1.65,
                  margin: "0 0 14px 0",
                }}
                dangerouslySetInnerHTML={{
                  __html: /<[a-z][\s\S]*>/i.test(item.description)
                    ? item.description
                    : item.description.replace(/\n/g, "<br/>"),
                }}
              />
            )}

            <div className="rv-pub-spec-table">
              {category?.categoryName && (
                <div className="rv-pub-spec-row">
                  <span className="rv-pub-spec-row-lbl">Category</span>
                  <span className="rv-pub-spec-row-val">{category.categoryName}</span>
                </div>
              )}
              {product?.purityStandard && (
                <div className="rv-pub-spec-row">
                  <span className="rv-pub-spec-row-lbl">Precious Metal Purity</span>
                  <span className="rv-pub-spec-row-val">{product.purityStandard}</span>
                </div>
              )}
              {item.displaySection && (
                <div className="rv-pub-spec-row">
                  <span className="rv-pub-spec-row-lbl">Collection</span>
                  <span className="rv-pub-spec-row-val">{item.displaySection}</span>
                </div>
              )}
              {product?.pricingType && (
                <div className="rv-pub-spec-row">
                  <span className="rv-pub-spec-row-lbl">Pricing Structure</span>
                  <span className="rv-pub-spec-row-val">{product.pricingType}</span>
                </div>
              )}
              {(product?.codeHSN || category?.codeHSN) && (
                <div className="rv-pub-spec-row">
                  <span className="rv-pub-spec-row-lbl">HSN Code</span>
                  <span className="rv-pub-spec-row-val">{product?.codeHSN || category?.codeHSN}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Related Items / Completer Pieces ── */}
      {relatedItems.length > 0 && (
        <section style={{ marginTop: 60 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <div>
              <h3 style={{ fontFamily: "var(--rv-font-serif)", fontSize: 24, fontWeight: 700, margin: 0, textTransform: "uppercase" }}>
                You May Also Like
              </h3>
              <p style={{ fontSize: 12.5, color: "#8F7B67", margin: "4px 0 0 0" }}>
                From the same collection — crafted in matching precious alloys
              </p>
            </div>
            <button type="button" className="rv-pub-btn-outline" onClick={onBack}>
              Explore Full Collection →
            </button>
          </div>

          <div className="rv-pub-grid">
            {relatedItems.slice(0, 4).map((rel) => {
              const relPId = Number(rel.productID ?? rel.productId ?? rel.ProductID ?? rel.id);
              const relItemId = rel.catalogueItemID ?? rel.catalogueItemId ?? rel.CatalogueItemID ?? rel.id;
              const relProd = productMap.get(relPId);
              const relPhotos = itemPhotosMap[relItemId] || [];
              const sorted = [...relPhotos].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
              const primaryObj = sorted.find((p) => p.isPrimary || p.IsPrimary) || sorted[0];
              const secondaryObj = sorted.find((p) => p !== primaryObj) || null;

              const getRelSrc = (p) => {
                if (!p) return null;
                return p.filepath?.includes("/") ? toAbsUrl(p.filepath) : toAbsUrl(p.photos?.[0]);
              };

              const relPrimarySrc = getRelSrc(primaryObj);
              const relSecondarySrc = getRelSrc(secondaryObj);
              const relCode = relProd?.productCode || "RJ-001";

              return (
                <div
                  key={relItemId}
                  className="rv-pub-card"
                  onClick={() => onSelectItem(rel)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="rv-pub-card-media">
                    {relPrimarySrc ? (
                      <>
                        <img src={relPrimarySrc} alt={rel.displayName} className="rv-pub-card-img" />
                        {relSecondarySrc && (
                          <img
                            src={relSecondarySrc}
                            alt={`${rel.displayName} alternate view`}
                            className="rv-pub-card-img rv-pub-card-img--secondary"
                          />
                        )}
                      </>
                    ) : (
                      <div className="rv-pub-card-no-img">
                        <span style={{ fontSize: 26 }}>💎</span>
                        <span>Fine Jewellery</span>
                      </div>
                    )}

                    {/* Featured badge (Commented out per UX review) */}
                    {/* {rel.isFeatured && (
                      <div className="rv-pub-card-top-badges">
                        <span className="rv-badge rv-badge--gold">⭐ FEATURED</span>
                      </div>
                    )} */}
                  </div>

                  <div className="rv-pub-card-body">
                    <div className="rv-pub-card-meta-row">
                      <span>{relCode}</span>
                    </div>
                    <h4 className="rv-pub-card-title">{rel.displayName}</h4>
                    <button type="button" className="rv-pub-btn-solid" style={{ marginTop: "auto", width: "100%" }}>
                      EXPLORE DESIGN →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── High-Definition Fullscreen Lightbox Overlay ── */}
      {isFullscreen && activeSrc && (
        <div
          className="rv-pub-lightbox-overlay"
          onClick={() => {
            setIsFullscreen(false);
            setIsZoomed(false);
          }}
        >
          {/* Top Bar */}
          <div className="rv-pub-lightbox-top-bar" onClick={(e) => e.stopPropagation()}>
            <div style={{ color: "#FFFFFF" }}>
              <div style={{ fontFamily: "var(--rv-font-serif)", fontSize: 20, fontWeight: 700 }}>
                {item.displayName}
              </div>
              <div style={{ fontSize: 11, color: "#C5A059", letterSpacing: "0.1em" }}>
                REFERENCE: {productCode} {safePhotos.length > 1 ? `· Angle ${activePhotoIdx + 1} of ${safePhotos.length}` : ""}
              </div>
            </div>

            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <button
                type="button"
                className="rv-pub-btn-outline"
                style={{ color: "#FFFFFF", borderColor: "rgba(255,255,255,0.4)", background: "rgba(0,0,0,0.4)" }}
                onClick={() => setIsZoomed((prev) => !prev)}
              >
                {isZoomed ? "Reset Zoom" : "Zoom In"}
              </button>
              <button
                type="button"
                className="rv-pub-btn-solid"
                style={{ background: "#FFFFFF", color: "#110F0E", borderColor: "#FFFFFF" }}
                onClick={() => {
                  setIsFullscreen(false);
                  setIsZoomed(false);
                }}
              >
                ✕ Close
              </button>
            </div>
          </div>

          {/* Lightbox Image Stage */}
          <div
            style={{
              maxWidth: "85vw",
              maxHeight: "80vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={activeSrc}
              alt={item.displayName}
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
                transform: isZoomed ? "scale(1.8)" : "scale(1)",
                transition: "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
                cursor: isZoomed ? "zoom-out" : "zoom-in",
              }}
              onClick={() => setIsZoomed((prev) => !prev)}
            />
          </div>

          {/* Navigation Arrows for Multiple Photos */}
          {safePhotos.length > 1 && (
            <>
              <button
                type="button"
                className="rv-pub-lightbox-nav-btn rv-pub-lightbox-nav-btn--prev"
                onClick={(e) => {
                  e.stopPropagation();
                  setActivePhotoIdx((prev) => (prev > 0 ? prev - 1 : safePhotos.length - 1));
                  setIsZoomed(false);
                }}
                title="Previous image"
              >
                ‹
              </button>
              <button
                type="button"
                className="rv-pub-lightbox-nav-btn rv-pub-lightbox-nav-btn--next"
                onClick={(e) => {
                  e.stopPropagation();
                  setActivePhotoIdx((prev) => (prev < safePhotos.length - 1 ? prev + 1 : 0));
                  setIsZoomed(false);
                }}
                title="Next image"
              >
                ›
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════
   MAIN PUBLIC SHOWCASE COMPONENT
════════════════════════════════════════════ */
export default function PublicCatalogue() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [itemPhotosMap, setItemPhotosMap] = useState({});
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [selectedSection, setSelectedSection] = useState("all");
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedAlloy, setSelectedAlloy] = useState("all");
  const [sortBy, setSortBy] = useState("curated");
  const [selectedModalItem, setSelectedModalItem] = useState(null);
  const [toast, setToast] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // LocalStorage-backed wishlist
  const [wishlist, setWishlist] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("reva_wishlist") || "[]");
    } catch {
      return [];
    }
  });

  const toggleWishlist = (itemId, displayName) => {
    setWishlist((prev) => {
      const exists = prev.includes(itemId);
      const next = exists ? prev.filter((id) => id !== itemId) : [...prev, itemId];
      try {
        localStorage.setItem("reva_wishlist", JSON.stringify(next));
      } catch { }
      setToast(exists ? `Removed "${displayName}" from Wishlist` : `Saved "${displayName}" to Wishlist ♥`);
      return next;
    });
  };

  const [searchParams, setSearchParams] = useSearchParams();

  /* ── Fetch all public data ── */
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const [itemsData, prodData, catData, allPhotosData] = await Promise.all([
          catalogueItemApi.getAll().catch(() => []),
          productApi.getAll().catch(() => []),
          productCategoryApi.getAll().catch(() => []),
          cataloguePhotoApi.getAll().catch(() => []),
        ]);

        const rawItems = Array.isArray(itemsData) ? itemsData : itemsData?.data ?? [];
        const activeItems = rawItems.filter((i) => i.isActive !== false);
        setItems(activeItems);

        const prodList = Array.isArray(prodData) ? prodData : prodData?.data ?? [];
        setProducts(prodList);

        const catList = Array.isArray(catData) ? catData : catData?.data ?? [];
        setCategories(catList);

        // Group photos by catalogue item ID
        const photosMap = {};
        const rawPhotos = Array.isArray(allPhotosData) ? allPhotosData : allPhotosData?.data ?? [];
        for (const ph of rawPhotos) {
          const itId = ph.catalogueItemID ?? ph.catalogueItemId ?? ph.CatalogueItemID;
          if (itId != null) {
            if (!photosMap[itId]) photosMap[itId] = [];
            photosMap[itId].push(ph);
          }
        }

        // Also fetch getByItemId for any active items to ensure complete photo isolation
        await Promise.all(
          activeItems.map(async (it) => {
            const itId = it.catalogueItemID ?? it.catalogueItemId ?? it.CatalogueItemID ?? it.id;
            if (itId != null && (!photosMap[itId] || photosMap[itId].length === 0)) {
              try {
                const phData = await cataloguePhotoApi.getByItemId(itId);
                const list = Array.isArray(phData) ? phData : phData?.data ?? [];
                if (list.length > 0) {
                  photosMap[itId] = list;
                }
              } catch {
                if (!photosMap[itId]) photosMap[itId] = [];
              }
            }
          })
        );
        setItemPhotosMap(photosMap);
      } catch (err) {
        console.error("Public catalogue error:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const { id: urlParamId } = useParams();

  /* ── Check direct link from URL query params (e.g. ?item=5 or ?code=RJ-001 or /catalogue/5) ── */
  useEffect(() => {
    const itemParam = urlParamId || searchParams.get("item");
    const codeParam = searchParams.get("code");
    if (items.length > 0) {
      if (itemParam) {
        const found = items.find((i) => String(i.catalogueItemID ?? i.id) === String(itemParam));
        if (found) setSelectedModalItem(found);
      } else if (codeParam) {
        const prod = products.find((p) => (p.productCode || "").toLowerCase() === codeParam.toLowerCase());
        if (prod) {
          const found = items.find((i) => i.productID === prod.productID);
          if (found) setSelectedModalItem(found);
        }
      }
    }
  }, [items, products, searchParams, urlParamId]);

  /* ── Fast lookup maps ── */
  const productMap = useMemo(() => {
    const map = new Map();
    for (const p of products) {
      const id = Number(p.productID ?? p.productId ?? p.ProductID ?? p.id);
      if (!isNaN(id)) map.set(id, p);
    }
    return map;
  }, [products]);

  const categoryMap = useMemo(() => {
    const map = new Map();
    for (const c of categories) {
      const id = Number(c.categoryID ?? c.categoryId ?? c.CategoryID ?? c.id);
      if (!isNaN(id)) map.set(id, c);
    }
    return map;
  }, [categories]);

  // Dynamic Featured Hero Items (for Top Spotlight Card)
  const [heroSlideIdx, setHeroSlideIdx] = useState(0);
  const [heroPhotoIdx, setHeroPhotoIdx] = useState(0);

  const featuredHeroItems = useMemo(() => {
    return items.filter((it) => {
      const isItemFeatured = Boolean(it.isFeatured || it.IsFeatured);
      const isHero = (it.displaySection || it.DisplaySection || "").toLowerCase().includes("hero");
      if (!isItemFeatured && !isHero) return false;
      const itemId = it.catalogueItemID ?? it.catalogueItemId ?? it.CatalogueItemID ?? it.id;
      const photos = itemPhotosMap[itemId] || itemPhotosMap[String(itemId)] || [];
      return photos.length > 0;
    });
  }, [items, itemPhotosMap]);

  const activeHeroItem =
    featuredHeroItems.length > 0
      ? featuredHeroItems[heroSlideIdx % featuredHeroItems.length]
      : null;

  // Auto-rotate featured hero items every 3 seconds
  useEffect(() => {
    if (featuredHeroItems.length <= 1) return;
    const interval = setInterval(() => {
      setHeroSlideIdx((prev) => (prev + 1) % featuredHeroItems.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [featuredHeroItems.length]);

  useEffect(() => {
    setHeroPhotoIdx(0);
  }, [heroSlideIdx]);

  /* ── Dynamic Filter Sections ── */
  const sections = useMemo(() => {
    const raw = items
      .map((i) => i.displaySection || i.DisplaySection)
      .filter(Boolean);
    return Array.from(new Set(raw));
  }, [items]);

  /* ── Dynamic Purity / Metal Options from Products API ── */
  const purityOptions = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      const std = (p.purityStandard || p.PurityStandard || "").trim();
      if (std) set.add(std);
    });
    const sorted = Array.from(set).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true })
    );
    return [
      { purityStandard: "all", label: "PURITY: ALL STANDARDS" },
      ...sorted.map((std) => ({
        purityStandard: std,
        label: std.toUpperCase(),
      })),
    ];
  }, [products]);

  /* ── Dynamic Collection Options for Multi-Select Dropdown ── */
  const collectionOptions = useMemo(() => {
    return [
      { section: "all", label: "COLLECTIONS: ALL CREATIONS" },
      ...sections.map((sec) => ({
        section: sec,
        label: sec.toUpperCase(),
      })),
    ];
  }, [sections]);

  /* ── Sort Options for SearchableDropdown ── */
  const sortOptions = [
    // { value: "curated", label: "SORT: ATELIER CURATED" },
    { value: "featured-first", label: "SORT: FEATURED" },
    { value: "name-asc", label: "SORT: DESIGN NAME (A-Z)" },
  ];

  /* ── Filtered & Sorted Showcase Items ── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);

    let res = items.filter((item) => {
      const pId = Number(item.productID ?? item.productId ?? item.ProductID ?? item.id);
      const itemId = item.catalogueItemID ?? item.catalogueItemId ?? item.CatalogueItemID ?? item.id;
      const prod = productMap.get(pId);
      const prodName = prod?.productName || prod?.ProductName || prod?.name || "";
      const prodCode = prod?.productCode || prod?.ProductCode || prod?.itemCode || prod?.code || "";
      const catId = Number(prod?.categoryID ?? prod?.categoryId ?? prod?.CategoryID);
      const cat = categoryMap.get(catId);
      const catName = cat?.categoryName || cat?.CategoryName || cat?.name || "";

      const displayName = item.displayName || item.DisplayName || item.name || "";
      const descLbl = item.descriptionLbl || item.DescriptionLbl || "";
      const desc = item.description || item.Description || "";
      const section = item.displaySection || item.DisplaySection || "";
      const purity = prod?.purityStandard || prod?.PurityStandard || "";
      const pricing = prod?.pricingType || prod?.PricingType || "";

      // Wishlist check
      if (selectedSection === "wishlist") {
        if (!wishlist.includes(itemId)) return false;
      }

      // Hero Section check (featured)
      const isItemFeatured = Boolean(item.isFeatured || item.IsFeatured);
      if (selectedSection === "featured" && !isItemFeatured) {
        return false;
      }

      // Collections Multi-Select check (displaySection)
      if (selectedCollections && selectedCollections.length > 0) {
        const matchCol = selectedCollections.some(
          (col) => col.trim().toLowerCase() === section.trim().toLowerCase()
        );
        if (!matchCol) return false;
      }

      // Category check
      if (selectedCategory !== "all") {
        const matchCat =
          String(catId) === String(selectedCategory) ||
          catName.toLowerCase() === selectedCategory.toLowerCase();
        if (!matchCat) return false;
      }

      // Purity Standard / Metal check
      if (selectedAlloy !== "all") {
        const target = selectedAlloy.toLowerCase();
        const prodPurity = (purity || "").toLowerCase();
        const matchesPurity =
          prodPurity === target ||
          prodPurity.includes(target) ||
          target.includes(prodPurity);

        if (!matchesPurity) {
          const combined = `${displayName} ${descLbl} ${catName} ${desc}`.toLowerCase();
          if (!combined.includes(target)) return false;
        }
      }

      // Search token matching
      if (tokens.length === 0) return true;

      const haystack = [
        displayName,
        descLbl,
        desc,
        section,
        prodName,
        prodCode,
        catName,
        purity,
        pricing,
      ]
        .join(" ")
        .toLowerCase();

      return tokens.every((token) => haystack.includes(token));
    });

    // Sorting
    if (sortBy === "name-asc") {
      res.sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));
    } else if (sortBy === "featured-first") {
      res.sort((a, b) => (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0));
    }

    return res;
  }, [items, search, selectedSection, selectedCollections, selectedCategory, selectedAlloy, sortBy, wishlist, productMap, categoryMap]);

  /* ── Share Item Link ── */
  const handleShare = (item, code) => {
    const itemId = item.catalogueItemID ?? item.catalogueItemId ?? item.CatalogueItemID ?? item.id;
    const shareUrl = `${window.location.origin}/catalogue?item=${itemId}`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(shareUrl);
      setToast(`Registry link copied to clipboard!`);
    } else {
      setToast(`Item Code: ${code}`);
    }
  };

  /* ── Back to Full Catalogue Grid ── */
  const handleBackToGrid = () => {
    setSelectedModalItem(null);
    setSearchParams({});
  };

  /* ── Helper to resolve primary & secondary photos ── */
  const getItemPhotos = (itemId) => {
    const photos =
      itemPhotosMap[itemId] ||
      itemPhotosMap[String(itemId)] ||
      itemPhotosMap[Number(itemId)] ||
      [];
    if (!photos.length) return { primary: null, secondary: null, count: 0 };
    const sorted = [...photos].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    const primaryObj = sorted.find((p) => p.isPrimary || p.IsPrimary) || sorted[0];
    const secondaryObj = sorted.find((p) => p !== primaryObj) || null;

    const getSrc = (p) => {
      if (!p) return null;
      const rawPath =
        p.filepath ||
        p.Filepath ||
        p.filename ||
        p.Filename ||
        (Array.isArray(p.photos) ? p.photos[0] : p.photos) ||
        (Array.isArray(p.Photos) ? p.Photos[0] : p.Photos);
      return rawPath && typeof rawPath === "string" ? toAbsUrl(rawPath) : null;
    };

    return {
      primary: getSrc(primaryObj),
      secondary: getSrc(secondaryObj),
      count: photos.length,
    };
  };

  return (
    <div className="rv-pub-cat">
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}

      {/* ── Main Atelier Navigation Header ── */}
      <header className="rv-pub-header">
        <div className="rv-pub-header-inner">
          <div className="rv-pub-brand" onClick={handleBackToGrid} style={{ cursor: "pointer" }}>
            <div className="rv-pub-logo-mark">
              <span className="rv-pub-logo-dot" />
              <span className="rv-pub-logo-dot--sm" />
            </div>
            <div className="rv-pub-brand-info">
              <span className="rv-pub-brand-text">REVA JEWELLERS</span>
              <span className="rv-pub-brand-subtext">HAUTE JOAILLERIE & SOLITAIRE ATELIER</span>
            </div>
          </div>

          {/* Capsule Switcher */}
          <div className="rv-pub-mode-capsule">
            <button type="button" className="rv-pub-mode-btn rv-pub-mode-btn--active">
              CLIENT DIGITAL SHOWCASE
            </button>
            <Link to="/dashboard" className="rv-pub-mode-btn rv-pub-mode-btn--inactive">
              MERCHANDISING STUDIO
            </Link>
          </div>

          {/* Desktop Navigation Links */}
          <nav className="rv-pub-nav-links rv-pub-nav-links--desktop">
            <button type="button" className="rv-pub-nav-item rv-pub-nav-item--active" onClick={handleBackToGrid}>
              ALL JEWELLERY
            </button>
            <button type="button" className="rv-pub-nav-item" onClick={() => setSelectedSection("featured")}>
              FEATURED
            </button>
            <button type="button" className="rv-pub-nav-item" onClick={() => setSelectedSection("all")}>
              COLLECTIONS
            </button>
            {wishlist.length > 0 && (
              <button
                type="button"
                className="rv-pub-nav-item"
                style={{ color: "#D4AF37", fontWeight: 700 }}
                onClick={() => setSelectedSection("wishlist")}
              >
                ♥ WISHLIST ({wishlist.length})
              </button>
            )}
          </nav>

          {/* Mobile Hamburger Toggle */}
          <button
            type="button"
            className="rv-pub-hamburger"
            onClick={() => setMobileNavOpen((prev) => !prev)}
            aria-label="Toggle navigation menu"
            aria-expanded={mobileNavOpen}
          >
            <span className={`rv-pub-hamburger-line ${mobileNavOpen ? "rv-pub-hamburger-line--open-1" : ""}`} />
            <span className={`rv-pub-hamburger-line ${mobileNavOpen ? "rv-pub-hamburger-line--open-2" : ""}`} />
            <span className={`rv-pub-hamburger-line ${mobileNavOpen ? "rv-pub-hamburger-line--open-3" : ""}`} />
          </button>
        </div>

        {/* Mobile Slide-Down Nav Drawer */}
        {mobileNavOpen && (
          <div className="rv-pub-mobile-nav" onClick={() => setMobileNavOpen(false)}>
            <button type="button" className="rv-pub-mobile-nav-item" onClick={handleBackToGrid}>
              ALL JEWELLERY
            </button>
            <button type="button" className="rv-pub-mobile-nav-item" onClick={() => setSelectedSection("featured")}>
              FEATURED
            </button>
            <button type="button" className="rv-pub-mobile-nav-item" onClick={() => setSelectedSection("all")}>
              COLLECTIONS
            </button>
            {wishlist.length > 0 && (
              <button
                type="button"
                className="rv-pub-mobile-nav-item"
                style={{ color: "#D4AF37", fontWeight: 700 }}
                onClick={() => setSelectedSection("wishlist")}
              >
                ♥ WISHLIST ({wishlist.length})
              </button>
            )}
          </div>
        )}
      </header>

      {/* ── Conditional Content: Single Product Showcase vs Full Catalogue Grid ── */}
      {selectedModalItem ? (
        <ProductDetailShowcase
          item={selectedModalItem}
          product={productMap.get(Number(selectedModalItem.productID ?? selectedModalItem.productId))}
          category={categoryMap.get(
            Number(
              productMap.get(Number(selectedModalItem.productID ?? selectedModalItem.productId))?.categoryID ??
              productMap.get(Number(selectedModalItem.productID ?? selectedModalItem.productId))?.categoryId
            )
          )}
          photos={
            itemPhotosMap[selectedModalItem.catalogueItemID ?? selectedModalItem.catalogueItemId ?? selectedModalItem.CatalogueItemID ?? selectedModalItem.id] ||
            itemPhotosMap[String(selectedModalItem.catalogueItemID ?? selectedModalItem.id)] ||
            []
          }
          relatedItems={items.filter(
            (it) =>
              (it.catalogueItemID ?? it.id) !== (selectedModalItem.catalogueItemID ?? selectedModalItem.id) &&
              (it.displaySection === selectedModalItem.displaySection || it.productID === selectedModalItem.productID)
          )}
          onBack={handleBackToGrid}
          onShare={handleShare}
          onSelectItem={(newItem) => {
            setSelectedModalItem(newItem);
            const nId = newItem.catalogueItemID ?? newItem.id;
            setSearchParams({ item: nId });
          }}
          itemPhotosMap={itemPhotosMap}
          productMap={productMap}
          wishlist={wishlist}
          onToggleWishlist={toggleWishlist}
        />
      ) : (
        <>
          {/* ── Editorial Hero Banner (Split Spotlight when featured items exist, centered otherwise) ── */}
          <section className="rv-pub-hero">
            {activeHeroItem ? (
              <div className="rv-pub-hero-split">
                {/* Left Column: Heading, Subheading, Search & Chips */}
                <div className="rv-pub-hero-split-left">
                  <div className="rv-pub-hero-intro">
                    <span className="rv-pub-hero-badge">
                      ✨ EXQUISITE HAUTE JOAILLERIE SHOWCASE
                    </span>
                    <h1 className="rv-pub-hero-title">
                      Timeless Luxury, <em>Handcrafted</em> for Generations
                    </h1>
                    <p className="rv-pub-hero-desc">
                      Explore hallmarked 18K & 22K gold, certified natural solitaire diamonds, and bespoke atelier creations curated directly from our Mayfair salon.
                    </p>
                  </div>

                  <div className="rv-pub-hero-actions">
                    {/* Luxury Search Group */}
                    <div className="rv-pub-search-group">
                      <svg className="rv-pub-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <input
                        type="text"
                        className="rv-pub-search-input"
                        placeholder="Search by hallmark, carat, cut, ID..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      {search && (
                        <button
                          type="button"
                          onClick={() => setSearch("")}
                          style={{ background: "none", border: "none", color: "#8F7B67", cursor: "pointer", marginRight: 8, fontSize: 13 }}
                        >
                          ✕
                        </button>
                      )}
                      <button
                        type="button"
                        className="rv-pub-search-btn"
                        onClick={() => document.getElementById("showcase")?.scrollIntoView({ behavior: "smooth" })}
                        aria-label="Discover collection"
                        title="Discover collection"
                      >
                        <span className="rv-pub-search-btn-text">DISCOVER</span>
                        <span className="rv-pub-search-btn-arrow">→</span>
                      </button>
                    </div>

                    {/* Quick Curation Filter Pills */}
                    <div className="rv-pub-hero-chips">
                      <button
                        type="button"
                        className={`rv-pub-chip ${selectedSection === "all" && selectedCollections.length === 0 ? "rv-pub-chip--active" : ""}`}
                        onClick={() => {
                          setSelectedSection("all");
                          setSelectedCollections([]);
                        }}
                      >
                        ALL CREATIONS ({items.length})
                      </button>
                      <button
                        type="button"
                        className={`rv-pub-chip rv-pub-chip--featured ${selectedSection === "featured" ? "rv-pub-chip--active" : ""}`}
                        onClick={() => setSelectedSection("featured")}
                      >
                        ⭐ FEATURED HIGHLIGHTS
                      </button>
                      {wishlist.length > 0 && (
                        <button
                          type="button"
                          className={`rv-pub-chip ${selectedSection === "wishlist" ? "rv-pub-chip--active" : ""}`}
                          onClick={() => setSelectedSection("wishlist")}
                          style={{ color: "#B8860B", borderColor: "#D4AF37" }}
                        >
                          ♥ MY WISHLIST ({wishlist.length})
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Column: Featured Hero Spotlight Card (Stitch Design) */}
                <div className="rv-pub-hero-split-right">
                  {(() => {
                    const heroItemId = activeHeroItem.catalogueItemID ?? activeHeroItem.catalogueItemId ?? activeHeroItem.CatalogueItemID ?? activeHeroItem.id;
                    const heroPhotos = itemPhotosMap[heroItemId] || itemPhotosMap[String(heroItemId)] || [];
                    const sorted = [...heroPhotos].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
                    const currentHeroPhoto = sorted[heroPhotoIdx % (sorted.length || 1)] || sorted[0];
                    const getHeroSrc = (p) => {
                      if (!p) return null;
                      const rawPath =
                        p.filepath ||
                        p.Filepath ||
                        p.filename ||
                        p.Filename ||
                        (Array.isArray(p.photos) ? p.photos[0] : p.photos) ||
                        (Array.isArray(p.Photos) ? p.Photos[0] : p.Photos);
                      return rawPath && typeof rawPath === "string" ? toAbsUrl(rawPath) : null;
                    };
                    const heroSrc = getHeroSrc(currentHeroPhoto);
                    const heroProd = productMap.get(Number(activeHeroItem.productID ?? activeHeroItem.productId));

                    return (
                      <div
                        className="rv-pub-hero-card"
                        onClick={() => {
                          setSelectedModalItem(activeHeroItem);
                          setSearchParams({ item: heroItemId });
                        }}
                      >
                        <AnimatePresence initial={false}>
                          {heroSrc ? (
                            <motion.img
                              key={heroItemId + "_" + (currentHeroPhoto?.photoID || heroPhotoIdx)}
                              src={heroSrc}
                              alt={activeHeroItem.displayName}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 1.0, ease: "easeInOut" }}
                              className="rv-pub-hero-card-img"
                            />
                          ) : (
                            <motion.div
                              key="no-img"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.8 }}
                              style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#B9A792" }}
                            >
                              <span>💎 Fine Haute Joaillerie</span>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <div className="rv-pub-hero-card-overlay">
                          <AnimatePresence mode="wait">
                            <motion.div
                              key={heroItemId}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -6 }}
                              transition={{ duration: 0.35, ease: "easeOut" }}
                            >
                              <h3 className="rv-pub-hero-card-title">{activeHeroItem.displayName}</h3>
                              {activeHeroItem.descriptionLbl && (
                                <p className="rv-pub-hero-card-desc">
                                  {activeHeroItem.descriptionLbl}
                                </p>
                              )}
                            </motion.div>
                          </AnimatePresence>

                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                            <span style={{ fontSize: 11, letterSpacing: "0.14em", fontWeight: 700, color: "#D4AF37", textTransform: "uppercase" }}>
                              EXPLORE MASTERPIECE →
                            </span>

                            {featuredHeroItems.length > 1 && (
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  className="rv-pub-hero-nav-dot-btn"
                                  onClick={() => setHeroSlideIdx((prev) => (prev > 0 ? prev - 1 : featuredHeroItems.length - 1))}
                                  title="Previous featured piece"
                                >
                                  ‹
                                </button>
                                <span style={{ fontSize: 10, color: "#FFFFFF", fontWeight: 700, letterSpacing: "0.08em" }}>
                                  {(heroSlideIdx % featuredHeroItems.length) + 1} / {featuredHeroItems.length}
                                </span>
                                <button
                                  type="button"
                                  className="rv-pub-hero-nav-dot-btn"
                                  onClick={() => setHeroSlideIdx((prev) => (prev < featuredHeroItems.length - 1 ? prev + 1 : 0))}
                                  title="Next featured piece"
                                >
                                  ›
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <div className="rv-pub-hero-inner">
                <span className="rv-pub-hero-badge">
                  ✨ EXQUISITE HAUTE JOAILLERIE SHOWCASE
                </span>
                <h1 className="rv-pub-hero-title">
                  Timeless Luxury, <em>Handcrafted</em> for Generations
                </h1>
                <p className="rv-pub-hero-desc">
                  Explore hallmarked 18K & 22K gold, certified natural solitaire diamonds, and bespoke atelier creations curated directly from our Mayfair salon.
                </p>

                {/* Luxury Search Group */}
                <div className="rv-pub-search-group">
                  <svg className="rv-pub-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    type="text"
                    className="rv-pub-search-input"
                    placeholder="Search by hallmark, carat, cut, ID..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      style={{ background: "none", border: "none", color: "#8F7B67", cursor: "pointer", marginRight: 8, fontSize: 13 }}
                    >
                      ✕
                    </button>
                  )}
                  <button
                    type="button"
                    className="rv-pub-search-btn"
                    onClick={() => document.getElementById("showcase")?.scrollIntoView({ behavior: "smooth" })}
                    aria-label="Discover collection"
                    title="Discover collection"
                  >
                    <span className="rv-pub-search-btn-text">DISCOVER</span>
                    <span className="rv-pub-search-btn-arrow">→</span>
                  </button>
                </div>

                {/* Quick Curation Filter Pills */}
                <div className="rv-pub-hero-chips">
                  <button
                    type="button"
                    className={`rv-pub-chip ${selectedSection === "all" && selectedCollections.length === 0 ? "rv-pub-chip--active" : ""}`}
                    onClick={() => {
                      setSelectedSection("all");
                      setSelectedCollections([]);
                    }}
                  >
                    ALL CREATIONS ({items.length})
                  </button>
                  <button
                    type="button"
                    className={`rv-pub-chip rv-pub-chip--featured ${selectedSection === "featured" ? "rv-pub-chip--active" : ""}`}
                    onClick={() => setSelectedSection("featured")}
                  >
                    ⭐ FEATURED HIGHLIGHTS
                  </button>
                  {wishlist.length > 0 && (
                    <button
                      type="button"
                      className={`rv-pub-chip ${selectedSection === "wishlist" ? "rv-pub-chip--active" : ""}`}
                      onClick={() => setSelectedSection("wishlist")}
                      style={{ color: "#B8860B", borderColor: "#D4AF37" }}
                    >
                      ♥ MY WISHLIST ({wishlist.length})
                    </button>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* ── Curated Showcase Controls Bar ── */}
          <div className="rv-pub-controls" id="showcase">
            <div className="rv-pub-controls-inner">
              <div className="rv-pub-controls-left">
                <span className="rv-pub-controls-title">Curated Showcase</span>
                <span className="rv-pub-controls-count">
                  SHOWING {filtered.length} OF {items.length} MASTER CREATIONS
                </span>
              </div>

              <div className="rv-pub-controls-right">
                {/* Category Filter — Searchable Dropdown */}
                <SearchableDropdown
                  theme="public"
                  options={[
                    { categoryID: "all", categoryName: "CATEGORY: ALL JEWELLERY" },
                    ...categories.map((c) => ({
                      categoryID: String(c.categoryID ?? c.categoryId ?? c.CategoryID ?? c.id),
                      categoryName: (c.categoryName || c.CategoryName || c.name || "").toUpperCase(),
                    }))
                  ]}
                  valueKey="categoryID"
                  labelKey="categoryName"
                  value={selectedCategory}
                  onChange={(val) => setSelectedCategory(val)}
                  placeholder="CATEGORY: ALL JEWELLERY"
                  searchPlaceholder="Search categories…"
                  pageSize={10}
                  allowClear={false}
                  className="rv-pub-sd-category"
                />

                {/* Collection / Section Multi-Select Filter — Searchable Dropdown */}
                {sections.length > 0 && (
                  <SearchableDropdown
                    theme="public"
                    isMulti={true}
                    options={collectionOptions}
                    valueKey="section"
                    labelKey="label"
                    value={selectedCollections}
                    onChange={(val) => setSelectedCollections(val)}
                    placeholder="COLLECTIONS: ALL"
                    searchPlaceholder="Search collections…"
                    pageSize={10}
                    allowClear={true}
                    className="rv-pub-sd-collection"
                  />
                )}

                {/* Purity / Metal Filter — Dynamic Searchable Dropdown */}
                <SearchableDropdown
                  theme="public"
                  options={purityOptions}
                  valueKey="purityStandard"
                  labelKey="label"
                  value={selectedAlloy}
                  onChange={(val) => setSelectedAlloy(val)}
                  placeholder="PURITY: ALL STANDARDS"
                  searchPlaceholder="Search purity standards…"
                  pageSize={10}
                  allowClear={false}
                  className="rv-pub-sd-purity"
                />

                {/* Sort Dropdown — Searchable Dropdown */}
                <SearchableDropdown
                  theme="public"
                  options={sortOptions}
                  valueKey="value"
                  labelKey="label"
                  value={sortBy}
                  onChange={(val) => setSortBy(val)}
                  placeholder="SORT: FEATURED"
                  searchPlaceholder="Search sorting…"
                  pageSize={10}
                  allowClear={false}
                  className="rv-pub-sd-sort"
                />

                {/* Reset Filters Shortcut when active */}
                {(selectedCategory !== "all" || selectedAlloy !== "all" || selectedSection !== "all" || selectedCollections.length > 0 || search || sortBy !== "curated") && (
                  <button
                    type="button"
                    className="rv-pub-btn-outline"
                    style={{ padding: "7px 12px", fontSize: 11 }}
                    onClick={() => {
                      setSearch("");
                      setSelectedSection("all");
                      setSelectedCollections([]);
                      setSelectedCategory("all");
                      setSelectedAlloy("all");
                      setSortBy("curated");
                    }}
                  >
                    ✕ Reset
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ── Product Showcase Grid with Motion Animations ── */}
          <main className="rv-pub-main">
            {loading ? (
              <div style={{ textAlign: "center", padding: "100px 0", color: "#8F7B67" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>💎</div>
                <p style={{ fontFamily: "var(--rv-font-serif)", fontSize: 20, fontWeight: 700, color: "#110F0E" }}>
                  Curating Reva High Jewellery Collection…
                </p>
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: "80px 20px", color: "#6E655F" }}>
                <p style={{ fontFamily: "var(--rv-font-serif)", fontSize: 24, fontWeight: 700, marginBottom: 8, color: "#110F0E" }}>
                  No Masterpieces Found
                </p>
                <p style={{ fontSize: 13, color: "#8F7B67", marginBottom: 20 }}>
                  {selectedSection === "wishlist"
                    ? "You haven't saved any jewellery creations to your wishlist yet."
                    : selectedAlloy === "Silver"
                      ? "No silver jewellery pieces found with the current filter settings."
                      : search
                        ? `No matching creations for "${search}". Try searching with a different term or registry code.`
                        : "No designs currently listed matching the selected category and metal criteria."}
                </p>
                <button
                  type="button"
                  className="rv-pub-btn-gold"
                  onClick={() => {
                    setSearch("");
                    setSelectedSection("all");
                    setSelectedCategory("all");
                    setSelectedAlloy("all");
                  }}
                >
                  Show All Creations
                </button>
              </div>
            ) : (
              <div className="rv-pub-grid">
                <AnimatePresence mode="popLayout">
                  {filtered.map((item, idx) => {
                    const pId = Number(item.productID ?? item.productId ?? item.ProductID ?? item.id);
                    const itemId = item.catalogueItemID ?? item.catalogueItemId ?? item.CatalogueItemID ?? item.id;
                    const prod = productMap.get(pId);
                    const { primary, secondary } = getItemPhotos(itemId);
                    const productCode = prod?.productCode || prod?.ProductCode || prod?.itemCode || "RJ-001";
                    const purity = prod?.purityStandard || "22K GOLD";
                    const categoryName = categoryMap.get(Number(prod?.categoryID ?? prod?.categoryId))?.categoryName || "SOLITAIRE";
                    const isWishlisted = wishlist.includes(itemId);

                    return (
                      <motion.div
                        key={itemId}
                        layout
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.3, delay: Math.min(idx * 0.03, 0.3), ease: [0.16, 1, 0.3, 1] }}
                        className="rv-pub-card"
                        onClick={() => {
                          setSelectedModalItem(item);
                          setSearchParams({ item: itemId });
                        }}
                      >
                        {/* Media Frame with Luxury Aspect Ratio & 2-Angle Hover */}
                        <div className="rv-pub-card-media">
                          {primary ? (
                            <>
                              <img src={primary} alt={item.displayName} className="rv-pub-card-img" />
                              {secondary && (
                                <img src={secondary} alt={`${item.displayName} alternate view`} className="rv-pub-card-img rv-pub-card-img--secondary" />
                              )}
                            </>
                          ) : (
                            <div className="rv-pub-card-no-img">
                              <span style={{ fontSize: 30 }}>💎</span>
                              <span>Fine Atelier Creation</span>
                            </div>
                          )}

                          {/* Simplified Single Badge + Wishlist */}
                          <div className="rv-pub-card-top-badges">
                            {/* Top-left badges (Commented out per UX review)
                            <div className="rv-pub-badge-stack">
                              {item.isFeatured ? (
                                <span className="rv-badge rv-badge--gold">⭐ FEATURED</span>
                              ) : (
                                <span className="rv-badge rv-badge--cert">BIS HALLMARKED</span>
                              )}
                            </div>
                            */}

                            <button
                              type="button"
                              className={`rv-pub-card-bookmark ${isWishlisted ? "rv-pub-card-bookmark--active" : ""}`}
                              title={isWishlisted ? "Remove from Wishlist" : "Save to Wishlist"}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleWishlist(itemId, item.displayName);
                              }}
                            >
                              {isWishlisted ? "♥" : "♡"}
                            </button>
                          </div>
                        </div>

                        {/* Card Body */}
                        <div className="rv-pub-card-body">
                          {/* Subtle Secondary Metadata */}
                          <div className="rv-pub-card-meta-row">
                            <span className="rv-pub-card-code">{productCode}</span>
                            <span className="rv-pub-card-cat">{categoryName}</span>
                          </div>

                          {/* Roomy Product Title */}
                          <h3 className="rv-pub-card-title">
                            {item.displayName}
                          </h3>

                          {/* Clean 2-Specification Row (No Awkward Wrapping) */}
                          <div className="rv-pub-spec-pill">
                            <div className="rv-pub-spec-col">
                              <span className="rv-pub-spec-lbl">PRECIOUS METAL</span>
                              <span className="rv-pub-spec-val">{purity}</span>
                            </div>
                            <div className="rv-pub-spec-col">
                              <span className="rv-pub-spec-lbl">
                                {prod?.grossWeight ? "GROSS WEIGHT" : "COLLECTION"}
                              </span>
                              <span className="rv-pub-spec-val">
                                {prod?.grossWeight ? `${prod.grossWeight}g` : (item.displaySection || "Showroom Signature")}
                              </span>
                            </div>
                          </div>

                          {/* Clear Pricing Area */}
                          {/* <div className="rv-pub-card-price-row">
                            <div className="rv-pub-card-price-box">
                              <span className="rv-pub-card-price">
                                {prod?.sellingPrice || prod?.price || item?.price
                                  ? `₹${Number(prod?.sellingPrice || prod?.price || item?.price).toLocaleString("en-IN")}`
                                  : prod?.pricingType === "Fixed"
                                    ? "Fixed Price"
                                    : "Price on Request"}
                              </span>
                            </div>
                          </div> */}

                          {/* Informative Primary Action */}
                          <div className="rv-pub-card-actions">
                            <button
                              type="button"
                              className="rv-pub-btn-solid"
                              style={{ width: "100%" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedModalItem(item);
                                setSearchParams({ item: itemId });
                              }}
                            >
                              EXPLORE DESIGN →
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </main>
        </>
      )}
    </div>
  );
}
